import moment from 'moment'
import React, { useState } from 'react'
import Select from 'react-select'
import { Link, Route, Switch } from 'react-router-dom'
import { DatePicker, Menu, Dropdown } from 'antd'

import { FaHandHolding } from 'react-icons/fa'
import { AiOutlineDown, AiOutlineReload } from 'react-icons/ai'

import { stringifyDate, useListData, useSocketEvents, useToggle, presets } from '../../modules'

import Receive from '../reusable/receive'
import Patient from '../reusable/patient'
import InpatientBadge from '../reusable/inpatientBadge'
import CallForPatient from '../reusable/callForPatient'

import PatientDetails from '../drawers/patient'
import Requests from '../drawers/requests'
import Forward from '../modals/forward'

const { RangePicker } = DatePicker

const BilledQueue = ({
    category,
    basePath,
    DrawerComponent,
    forwardSource = category,
    forwardDestination = 'Doctor',
    requestDepartment = category,
    extraLinks = [],
    extraRoutes = [],
    includeReportsMenu = false,
    usePresetRanges = true,
    showPendingForwardsBadge = false,
}) => {
    const [ranges, setRanges] = useState([moment().startOf('day'), moment().endOf('day')])
    const [chosen, setChosen] = useState([])
    const [type, setType] = useState({ label: 'All', value: 'All' })
    const [string, setString] = useState('')
    const [selected, setSelected] = useState('')
    const [notSeen, toggleNSeen] = useToggle(true)
    const [select] = useState(false)
    const [forward, toggleForward] = useToggle()

    const [
        { loading: qLoading, error: qError, fetch: fetchBillItems },
        { list: queue },
        { page = 1, limit = 10, navigate, Paginate },
    ] = useListData(
        'billedItemsByCategory',
        ranges
            ? {
                  category,
                  item: type?.value !== 'All' ? type?.value : undefined,
                  string,
                  pendingOnly: notSeen,
                  paidOnly: true,
                  from: ranges[0]?._d ?? null,
                  to: ranges[1]?._d ?? null,
                  order: notSeen ? 1 : -1,
              }
            : {
                  category,
                  item: type?.value !== 'All' ? type?.value : undefined,
                  string,
                  pendingOnly: notSeen,
                  paidOnly: true,
                  order: notSeen ? 1 : -1,
              },
        { notify: true },
        'items',
    )

    const [{ loading: sILoading, error: sIError }, { list: saleItems = [] }] = useListData(
        'pointOfSaleItems',
        { category, string: '' },
        { notify: true },
        'items',
    )

    const choose = id => {
        let temp = [...chosen]

        if (temp.includes(id)) temp = temp.filter(x => x !== id)
        else temp.push(id)

        setChosen(temp)
    }

    const refresh = () => {
        navigate(1)
        fetchBillItems()
    }

    useSocketEvents({
        'new-patient': {
            condition: data => data.destination === category,
            notif: {
                message: 'New Patient',
                type: 'info',
            },
            callback: refresh,
        },
    })

    const filterByType = typ => {
        setType(typ)
        refresh()
    }

    const search = str => {
        setString(str)
        refresh()
    }

    const selectRange = range => {
        setRanges(range)
        refresh()
    }

    const toggleNotSeen = () => {
        toggleNSeen()
        refresh()
    }

    const forwardPatient = item => {
        setSelected(item._id)
        toggleForward()
    }

    return (
        <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
            <div className="sm:flex sm:justify-between sm:items-center mb-8">
                <input
                    onChange={e => search(e.target.value)}
                    className="appearance-none block max-w-4xl bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                    id="grid-search"
                    type="text"
                    placeholder="Patient Name, OP/IP No." />
                <div className="grid grid-flow-col sm:auto-cols-max justify-start sm:justify-end gap-2">
                    <RangePicker
                        value={usePresetRanges ? ranges : undefined}
                        onChange={selectRange}
                        ranges={usePresetRanges ? presets : undefined}
                    />
                    {includeReportsMenu && (
                        <Dropdown
                            overlay={
                                <Menu>
                                    <Menu.Item onClick={() => null}>Patient Visits</Menu.Item>
                                </Menu>
                            }>
                            <button className="btn hisani-btn-green-light ml-2 text-white">
                                <span className="hidden xs:block mr-2">Reports</span>
                                <AiOutlineDown />
                            </button>
                        </Dropdown>
                    )}
                </div>
            </div>
            <Forward
                source={forwardSource}
                item={selected}
                toggle={toggleForward}
                visible={forward}
                destination={forwardDestination}
            />
            <div className="col-span-full xl:col-span-8 bg-white shadow-lg rounded-sm border border-gray-200">
                <header className="px-5 py-4 border-b border-gray-100 flex justify-between items-center">
                    <span>
                        <h2 className="font-semibold text-gray-800 mb-1">Patients' Queue</h2>
                        <span>
                            <input
                                onChange={toggleNotSeen}
                                checked={notSeen}
                                type="checkbox"
                                className="form-checkbox cursor-pointer h-5 w-5" />
                            <span className="text-gray-500 text-xs italic ml-2">Only Show Pending</span>
                        </span>
                    </span>
                    <span className="flex items-center justify-between">
                        <Receive loading={sILoading} error={sIError}>
                            <Select
                                value={type}
                                onChange={filterByType}
                                styles={{ input: styles => ({ ...styles, minWidth: '10em' }) }}
                                options={[{ label: 'All', value: 'All' }, ...saleItems.map(item => ({ label: item.name, value: item._id }))]}
                                placeholder="Filter By Type" />
                        </Receive>
                        <button onClick={refresh} className="btn ml-2 py-2.5 hisani-btn-green-light text-white">
                            <AiOutlineReload style={{ color: 'white' }} />
                        </button>
                        <Link className="ml-2" to={`${basePath}/requests`}>
                            <button className="btn hisani-btn-green-light text-white flex items-center justify-between">
                                <FaHandHolding />
                                <span className="xs:block ml-2">Requests</span>
                            </button>
                        </Link>
                        {extraLinks.map(link => (
                            <Link className="ml-2" to={link.to} key={link.to}>
                                <button className="btn hisani-btn-green-light text-white flex items-center justify-between">
                                    {link.icon ?? <FaHandHolding />}
                                    <span className="xs:block ml-2">{link.label}</span>
                                </button>
                            </Link>
                        ))}
                    </span>
                </header>
                <div className="p-3">
                    <Receive progress loading={qLoading} error={qError}>
                        {queue?.length > 0 ? (
                            <div className="overflow-x-auto">
                                <table className="table-auto w-full">
                                    <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                                        <tr>
                                            <th className="p-2">
                                                <div className="font-semibold text-center">{select ? 'Selected' : 'No'}</div>
                                            </th>
                                            <th className="p-2">
                                                <div className="font-semibold text-left">Patient Name</div>
                                            </th>
                                            <th className="p-2">
                                                <div className="font-semibold text-left">Paid For</div>
                                            </th>
                                            <th className="p-2">
                                                <div className="font-semibold text-center">Sent On</div>
                                            </th>
                                            <th className="p-2">
                                                <div className="font-semibold text-center"></div>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-sm font-medium divide-y divide-gray-100">
                                        {queue.map((item, key) => (
                                            <tr key={item._id}>
                                                <td className="p-2">
                                                    <div className="text-center">
                                                        {select ? (
                                                            <input
                                                                onChange={() => choose(item._id)}
                                                                checked={chosen.includes(item._id)}
                                                                type="checkbox"
                                                                className="form-checkbox cursor-pointer h-5 w-5" />
                                                        ) : (
                                                            `${(page - 1) * limit + (key + 1)}.`
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="p-2">
                                                    <Patient patient={item?.patient} />
                                                </td>
                                                <td className="p-2">
                                                    <div className="text-left">
                                                        {item.ref?.name ?? item.name}
                                                        {item.quantity > 1 && ` (x ${item.quantity})`}
                                                    </div>
                                                    <div className="flex space-x-2 pl-1 items-center">
                                                        <div className={`w-2 h-2 rounded-full bg-${item.seen ? 'green' : item.discarded ? 'gray' : 'yellow'}-500 shadow`}></div>
                                                        <p className={`text-${item.seen ? 'green' : item.discarded ? 'gray' : 'yellow'}-500 text-xs italic mb-0`}>
                                                            {item.seen ? 'Seen' : item.discarded ? 'Discarded' : 'Pending'}
                                                        </p>
                                                    </div>
                                                </td>
                                                <td className="p-2">
                                                    <div className="text-center">{stringifyDate(item.date)}</div>
                                                </td>
                                                <td className="p-2">
                                                    <InpatientBadge show={item.order.inPatient}>
                                                        <div className="text-center">
                                                            <Dropdown
                                                                overlay={
                                                                    <Menu>
                                                                        <Link to={`${basePath}/${item._id}`}><Menu.Item>{item.filled ? 'Review' : 'View'} Request</Menu.Item></Link>
                                                                        <Link to={`${basePath}/patients/${item.patient?._id}`}><Menu.Item>Patient Details</Menu.Item></Link>
                                                                        <Menu.Item onClick={() => forwardPatient(item)}>Forward Patient</Menu.Item>
                                                                        <CallForPatient order={item.order._id} patient={item.patient} />
                                                                    </Menu>
                                                                }>
                                                                <button type="button" className="ant-dropdown-link flex items-center h-10">
                                                                    <span className="mr-2">Actions</span> <AiOutlineDown />
                                                                </button>
                                                            </Dropdown>
                                                        </div>
                                                    </InpatientBadge>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="w-full h-16 flex items-center justify-center">
                                <p className="font-semibold text-base">No patients {string.trim().length > 2 ? `matching "${string}"` : ''} were found</p>
                            </div>
                        )}
                    </Receive>
                    {Paginate}
                </div>
            </div>
            <Switch>
                <Route path={`${basePath}/requests`}>
                    <Requests department={requestDepartment} path={basePath} />
                </Route>
                {extraRoutes.map((route, index) => (
                    <Route key={index} path={route.path}>
                        {route.component(refresh)}
                    </Route>
                ))}
                <Route path={`${basePath}/patients/:id`}>
                    <PatientDetails reload={refresh} source={basePath} />
                </Route>
                <Route path={`${basePath}/:id`}>
                    <DrawerComponent onClose={refresh} />
                </Route>
            </Switch>
            {showPendingForwardsBadge && (
                <button className="p-0 text-white font-extrabold w-12 h-12 bg-grey-600 rounded-full hover:bg-grey-700 active:shadow-lg mouse fixed bottom-5 right-10 shadow transition ease-in duration-200 focus:outline-none">
                    0
                </button>
            )}
        </div>
    )
}

export default BilledQueue
